import React, { useEffect, useState } from "react";
import { TouchableOpacity, Text, View } from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppColors } from "../../utility/AppColors";
import { Image } from "react-native";
import { AppImages } from "../../utility/AppImages";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";
import ApplyAsInfluencerModal from "./ApplyAsInfluencerModal";
import { useNotifications } from "../../context/NotificationContext";
import { currentEnv } from "../../Api/configs";

const CreateEventComponent = ({
  isMyEvents,
  setIsMyEvents,
  setShowEventTypeModal,
  userData,
}) => {
  const deviceSize = useDeviceSize();
  const [isInfluencer, setIsInfluencer] = useState<boolean | null>(null);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfluencerModal, setShowInfluencerModal] = useState(false);

  // Listen for notifications to auto-refresh when approved
  const { newNotification } = useNotifications();

  // Function to fetch role status - can be called to refresh
  const checkInfluencerRole = async () => {
    if (!userData?.AccessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${currentEnv.apiUrl}user/role?accessToken=${userData.AccessToken}`
      );
      const data = await response.json();
      setIsInfluencer(data.isInfluencer === true);
      setApplicationSubmitted(data.applicationSubmitted === true);
      setApprovalStatus(data.approvalStatus || null);
    } catch (error) {
      console.error("Failed to check influencer role:", error);
      setIsInfluencer(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkInfluencerRole();
  }, [userData?.AccessToken]);

  // Auto-refresh role status when account_update notification is received
  useEffect(() => {
    if (newNotification?.type === "account_update") {
      const notificationType = newNotification?.data?.type;
      if (notificationType === "influencer_approved" || notificationType === "influencer_rejected") {
        console.log("Account update notification received, refreshing role status...");
        checkInfluencerRole();
      }
    }
  }, [newNotification]);

  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Show Create Event button for influencers
  if (isInfluencer === true) {
    return (
      <TouchableOpacity
        style={{
          backgroundColor: AppColors.appViolentColor_6D08C3,
          borderRadius: 10,
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          paddingVertical: 12,
          paddingHorizontal: deviceSize === DEVICE_SIZES.XS ? 12 : 24,
          gap: deviceSize === DEVICE_SIZES.XS ? 5 : 8,
        }}
        onPress={() => {
          setShowEventTypeModal(true);
        }}
      >
        <Text
          style={[
            deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
              ? AppFontStyle.BOLD_ANYBODY_12
              : AppFontStyle.BOLD_ANYBODY_17,
            {
              color: AppColors.white,
              minWidth: 86,
            },
          ]}
        >
          Create Event
        </Text>
        <Image
          source={AppImages.icPlusIcon}
          style={{
            height: 14,
            width: 14,
            tintColor: AppColors.white,
            resizeMode: "contain",
          }}
        />
      </TouchableOpacity>
    );
  }

  // Show pending approval status when application is submitted
  if (applicationSubmitted && approvalStatus === "pending") {
    return (
      <View
        style={{
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 8 : 16,
        }}
      >
        <View style={{ flexDirection: "column", gap: 2, flex: 1, flexShrink: 1 }}>
          <Text
            style={{
              color: "#FACC15",
              fontSize: isMobile ? 11 : 14,
              fontWeight: "700",
              lineHeight: isMobile ? 14 : 20,
            }}
            numberOfLines={isMobile ? 2 : 1}
          >
            Kallout beta approval is pending.
          </Text>
          <Text
            style={{
              color: "#FFF",
              fontSize: isMobile ? 10 : 14,
              fontWeight: "400",
              lineHeight: isMobile ? 13 : 18,
            }}
            numberOfLines={isMobile ? 2 : 1}
          >
            Once approved, you'll have access.
          </Text>
        </View>

        <View
          style={{
            paddingVertical: isMobile ? 6 : 12,
            paddingHorizontal: isMobile ? 10 : 20,
            justifyContent: "center",
            alignItems: "center",
            alignSelf: isMobile ? "flex-end" : "center",
            borderRadius: 6,
            overflow: "hidden",
            opacity: 0.5,
          }}
        >
          <LinearGradient
            colors={["#DC2626", "#991B1B"]}
            start={[0, 0]}
            end={[1, 0]}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <Text
            style={{
              color: "#FFF",
              fontSize: isMobile ? 10 : 16,
              fontWeight: "700",
              lineHeight: isMobile ? 14 : 20,
            }}
          >
            Create Event
          </Text>
        </View>
      </View>
    );
  }

  // Show rejection status when application was rejected
  if (applicationSubmitted && approvalStatus === "rejected") {
    return (
      <View
        style={{
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 8 : 16,
        }}
      >
        <View style={{ flexDirection: "column", gap: 2, flex: 1, flexShrink: 1 }}>
          <Text
            style={{
              color: "#DC2626",
              fontSize: isMobile ? 11 : 14,
              fontWeight: "700",
              lineHeight: isMobile ? 14 : 20,
            }}
            numberOfLines={isMobile ? 2 : 1}
          >
            Kallout beta request application Rejected.
          </Text>
          <Text
            style={{
              color: "#FFF",
              fontSize: isMobile ? 10 : 14,
              fontWeight: "400",
              lineHeight: isMobile ? 13 : 18,
            }}
            numberOfLines={isMobile ? 2 : 1}
          >
            You can apply after some time.
          </Text>
        </View>

        <View
          style={{
            paddingVertical: isMobile ? 6 : 12,
            paddingHorizontal: isMobile ? 10 : 20,
            justifyContent: "center",
            alignItems: "center",
            alignSelf: isMobile ? "flex-end" : "center",
            borderRadius: 6,
            overflow: "hidden",
            opacity: 0.5,
          }}
        >
          <LinearGradient
            colors={["#DC2626", "#991B1B"]}
            start={[0, 0]}
            end={[1, 0]}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <Text
            style={{
              color: "#FFF",
              fontSize: isMobile ? 10 : 16,
              fontWeight: "700",
              lineHeight: isMobile ? 14 : 20,
            }}
          >
            Apply as influencer
          </Text>
        </View>
      </View>
    );
  }

  // Show Apply as Influencer CTA for non-influencers
  if (isInfluencer === false) {
    return (
      <>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: isMobile ? 8 : 16,
          }}
        >
          <View style={{ flexDirection: "column", gap: 2 }}>
            <Text
              style={{
                color: "#DC2626",
                fontSize: isMobile ? 12 : 14,
                fontWeight: "700",
                lineHeight: isMobile ? 16 : 20,
              }}
            >
              Create event
            </Text>
            <Text
              style={{
                color: "#FFF",
                fontSize: isMobile ? 10 : 14,
                fontWeight: "400",
                lineHeight: isMobile ? 14 : 18,
              }}
            >
              {isMobile ? "Apply as influencer" : "To create event, apply as influencer"}
            </Text>
          </View>

          <TouchableOpacity
            onPress={() => {
              setShowInfluencerModal(true);
            }}
            style={{
              paddingVertical: isMobile ? 6 : 10,
              paddingHorizontal: isMobile ? 6 : 8,
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <LinearGradient
              colors={["#DC2626", "#991B1B"]}
              start={[0, 0]}
              end={[1, 0]}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            />
            <Text
              style={{
                color: "#FFF",
                fontSize: isMobile ? 10 : 16,
                fontWeight: "700",
                lineHeight: isMobile ? 14 : 20,
              }}
            >
              Apply as influncer
            </Text>
          </TouchableOpacity>
        </View>

        <ApplyAsInfluencerModal
          visible={showInfluencerModal}
          onClose={() => setShowInfluencerModal(false)}
          onSuccess={checkInfluencerRole}
          accessToken={userData?.AccessToken}
        />
      </>
    );
  }

  // Loading state - return null or a placeholder
  return null;
};

export default CreateEventComponent;


